import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { showError } from '@/lib/alert'
import { usePageTitle } from '@/hooks/usePageTitle'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  careerOptions,
  companyOptions,
  positionOptions,
  useCareers,
  useCompanies,
  usePositions,
} from '@/features/masterData/queries'

/**
 * Port of Views/Home/Index.cshtml + wwwroot/js/app/home/home.js.
 *
 * The page is a stack of full-width landing images with one search card
 * overlapping the first. Image order and the `-50px` overlap are copied exactly;
 * image 13 stays commented out, as it was in the Razor.
 *
 * home.js also constructed a Swiper over `.landing-gallery-swiper`. No element
 * with that class exists in this view — leftover from the template — so it is
 * dropped rather than carried over as a no-op.
 */
const LANDING_IMAGES = ['2.png', '11.png', '12.png', '3.png', '4.png', '5.png', '6.png', '8.png']

export function Home(): JSX.Element {
  usePageTitle('Home Page')
  const navigate = useNavigate()

  const [company, setCompany] = useState('')
  const [position, setPosition] = useState('')
  const [career, setCareer] = useState('')
  const [isSearching, setIsSearching] = useState(false)

  const careers = useCareers()
  const companies = useCompanies()
  const positions = usePositions()

  async function handleSearch(): Promise<void> {
    setIsSearching(true)
    try {
      // Home.GoToVacancy: stash the three filters server-side (they were
      // TempData), then land on the listing, which reads them back.
      // Blank means "All" and is sent as 0, exactly as the original did.
      await api.post('/api/ecareer/vacancy/search', {
        mstCompanyID: company === '' ? 0 : Number(company),
        mstCareerID: career === '' ? 0 : Number(career),
        mstPositionID: position === '' ? 0 : Number(position),
      })
      navigate('/Vacancy')
    } catch (error) {
      await showError(error instanceof Error ? error.message : 'Search failed')
    } finally {
      setIsSearching(false)
    }
  }

  return (
    <>
      <section className="bg-white pt-0 pb-0">
        <div className="container">
          <div className="row justify-content-center text-center">
            <img src="/lib/images/landing/1.png" alt="" />
          </div>
        </div>
      </section>

      <section className="bg-white pt-0 pb-0">
        <div className="container">
          <div className="row justify-content-center button">
            <div className="col-11 col-sm-11 col-xl-11">
              <div className="card contact-card-form user-card" style={{ marginTop: -50 }}>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="row">
                        <div className="col-md-4 mb-2 p-1">
                          <SearchableSelect
                            id="slscompany"
                            options={companyOptions(companies.data?.Data)}
                            value={company}
                            onChange={setCompany}
                            placeholder="Choose Company"
                          />
                        </div>
                        <div className="col-md-4 mb-2 p-1">
                          <SearchableSelect
                            id="slsposition"
                            options={positionOptions(positions.data?.Data)}
                            value={position}
                            onChange={setPosition}
                            placeholder="Choose Position"
                          />
                        </div>
                        <div className="col-md-3 mb-2 p-1">
                          <SearchableSelect
                            id="slscareer"
                            options={careerOptions(careers.data?.Data)}
                            value={career}
                            onChange={setCareer}
                            placeholder="Choose Career"
                          />
                        </div>
                        <div className="col-md-1 mb-2 p-1 text-center">
                          <button
                            type="button"
                            className="btn btn-primary d-inline-flex"
                            id="btnSearch"
                            disabled={isSearching}
                            onClick={() => void handleSearch()}
                          >
                            <i className="ti ti-search me-1" />
                            Search
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {LANDING_IMAGES.map((image) => (
        <section key={image} className="bg-white pt-0 pb-0">
          <div className="container">
            <div className="row justify-content-center text-center">
              <img src={`/lib/images/landing/${image}`} alt="" />
            </div>
          </div>
        </section>
      ))}
    </>
  )
}
